import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import { formatPrice, getImageUrl } from '../utils/currency';
import { Building, Building2, Package, ShoppingCart, Search, Filter, Globe, Plus, X, Mail, Phone, MapPin, Calendar, Tag, DollarSign, Hash, Lock, LogIn, SlidersHorizontal, ArrowUpDown, RotateCcw, ChevronDown, ChevronLeft, ChevronRight, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import countries, { getCountryByCode } from '../data/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MarketplacePage() {
  const { t, language, toggleLanguage } = useLanguage();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('offers');
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [searchParams] = useSearchParams();
  const [offersTotal, setOffersTotal] = useState(0);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [offersPage, setOffersPage] = useState(1);
  const [requestsPage, setRequestsPage] = useState(1);
  const [offersTotalPages, setOffersTotalPages] = useState(1);
  const [requestsTotalPages, setRequestsTotalPages] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const debounceRef = useRef(null);

  const hasActiveFilters = searchQuery || selectedCategory || selectedCountry || minPrice || maxPrice || sortBy !== 'newest';

  // Build query params
  const buildParams = useCallback((page) => {
    const params = new URLSearchParams();
    params.set('status', 'approved');
    params.set('page', page);
    params.set('page_size', '12');
    if (searchQuery) params.set('q', searchQuery);
    if (selectedCategory) params.set('category_id', selectedCategory);
    if (selectedCountry) params.set('country', selectedCountry);
    if (sortBy && sortBy !== 'newest') params.set('sort_by', sortBy);
    return params;
  }, [searchQuery, selectedCategory, selectedCountry, sortBy]);

  const buildOfferParams = useCallback((page) => {
    const params = buildParams(page);
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);
    return params;
  }, [buildParams, minPrice, maxPrice]);

  const buildRequestParams = useCallback((page) => {
    const params = buildParams(page);
    if (minPrice) params.set('min_budget', minPrice);
    if (maxPrice) params.set('max_budget', maxPrice);
    return params;
  }, [buildParams, minPrice, maxPrice]);

  const fetchOffers = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/offers?${buildOfferParams(page)}`);
      setOffers(res.data.items || []);
      setOffersTotal(res.data.total || 0);
      setOffersTotalPages(res.data.total_pages || 1);
      setOffersPage(page);
    } catch (e) { console.error(e); }
  }, [buildOfferParams]);

  const fetchRequests = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/requests?${buildRequestParams(page)}`);
      setRequests(res.data.items || []);
      setRequestsTotal(res.data.total || 0);
      setRequestsTotalPages(res.data.total_pages || 1);
      setRequestsPage(page);
    } catch (e) { console.error(e); }
  }, [buildRequestParams]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      try {
        const [, , catRes] = await Promise.all([
          fetchOffers(1),
          fetchRequests(1),
          axios.get(`${API}/categories`)
        ]);
        setCategories(catRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch on filter change (with debounce for search)
  useEffect(() => {
    if (loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchOffers(1);
      fetchRequests(1);
    }, searchQuery ? 400 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, selectedCategory, selectedCountry, minPrice, maxPrice, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedCountry('');
    setMinPrice('');
    setMaxPrice('');
    setSortBy('newest');
  };

  const openDetail = async (id, type) => {
    setDetailLoading(true);
    setModalImageIndex(0);
    setReviews([]); setReviewRating(0); setReviewComment('');
    try {
      const endpoint = type === 'offer' ? 'offers' : 'requests';
      const { data } = await axios.get(`${API}/${endpoint}/${id}`);
      setSelectedItem({ ...data, type });
      // Load reviews for this company
      if (data.company_id) {
        try {
          const revRes = await axios.get(`${API}/reviews/${data.company_id}`);
          setReviews(revRes.data.items || []);
        } catch (e) {}
      }
    } catch (error) {
      console.error('Error fetching detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const submitReview = async () => {
    if (!selectedItem?.company_id || reviewRating === 0) return;
    setReviewSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, { company_id: selectedItem.company_id, rating: reviewRating, comment: reviewComment });
      toast.success(language === 'ar' ? 'تم إضافة التقييم بنجاح' : 'Review submitted!');
      const revRes = await axios.get(`${API}/reviews/${selectedItem.company_id}`);
      setReviews(revRes.data.items || []);
      setReviewRating(0); setReviewComment('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || (language === 'ar' ? 'فشل إضافة التقييم' : 'Failed to submit review'));
    } finally { setReviewSubmitting(false); }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm(language === 'ar' ? 'هل تريد حذف هذا التقييم؟' : 'Delete this review?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      toast.success(language === 'ar' ? 'تم حذف التقييم' : 'Review deleted');
    } catch (err) { toast.error(language === 'ar' ? 'فشل الحذف' : 'Delete failed'); }
  };

  const renderStars = (rating, size = 16) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} size={size} className={i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'} />
      ))}
    </div>
  );

  const sortOptions = [
    { value: 'newest', label_ar: 'الأحدث', label_en: 'Newest' },
    { value: 'oldest', label_ar: 'الأقدم', label_en: 'Oldest' },
    { value: 'price_asc', label_ar: 'السعر: من الأقل', label_en: 'Price: Low to High' },
    { value: 'price_desc', label_ar: 'السعر: من الأعلى', label_en: 'Price: High to Low' },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="marketplace-page">
      {user ? (
        <AppHeader />
      ) : (
        <header className="bg-white border-b border-border sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-accent to-accent/70 rounded-lg flex items-center justify-center">
                  <Building2 size={20} className="text-white sm:hidden" />
                  <Building2 size={24} className="text-white hidden sm:block" />
                </div>
                <span className="text-xl sm:text-2xl font-heading font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{t('appName')}</span>
              </Link>
              <div className="flex gap-2 sm:gap-4 items-center">
                <button onClick={toggleLanguage} className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="language-toggle">
                  <Globe size={18} />
                  {language === 'ar' ? 'English' : 'عربي'}
                </button>
                <Link to="/login" data-testid="login-link">
                  <button className="hidden sm:inline-block px-6 py-2 text-sm font-medium text-foreground hover:text-primary">{t('login')}</button>
                </Link>
                <Link to="/register" data-testid="register-link">
                  <button className="px-4 sm:px-6 py-2 text-xs sm:text-sm font-medium bg-accent text-accent-foreground rounded-lg hover:bg-accent/90">{t('register')}</button>
                </Link>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-2xl sm:text-4xl font-heading font-bold text-primary mb-1 sm:mb-2">{t('marketplace')}</h1>
            <p className="text-sm sm:text-base text-muted-foreground">{t('browseMarketplace')}</p>
          </div>
          {user && user.company?.status === 'approved' && (
            <div className="flex gap-2 sm:gap-3" data-testid="marketplace-action-buttons">
              <Link to="/offers/new" data-testid="marketplace-create-offer">
                <button className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-accent text-white rounded-lg hover:bg-accent/90 font-semibold text-xs sm:text-sm transition-all shadow-sm hover:shadow-md">
                  <Plus size={16} />
                  {t('createOffer')}
                </button>
              </Link>
              <Link to="/requests/new" data-testid="marketplace-create-request">
                <button className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-xs sm:text-sm transition-all shadow-sm hover:shadow-md">
                  <Plus size={16} />
                  {t('createRequest')}
                </button>
              </Link>
            </div>
          )}
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-border shadow-sm mb-6 sm:mb-8 overflow-hidden" data-testid="filters-section">
          {/* Main search row */}
          <div className="p-4 sm:p-5">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  data-testid="search-input"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-gray-50/50`}
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-filters-btn"
                className={`flex items-center gap-2 px-4 py-3 border rounded-lg font-medium text-sm transition-all ${
                  showFilters || hasActiveFilters
                    ? 'border-accent bg-accent/5 text-accent'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-gray-300'
                }`}
              >
                <SlidersHorizontal size={18} />
                <span className="hidden sm:inline">{language === 'ar' ? 'فلاتر' : 'Filters'}</span>
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-accent" />
                )}
              </button>
            </div>
          </div>

          {/* Expandable filters */}
          {showFilters && (
            <div className="px-4 sm:px-5 pb-5 pt-0 border-t border-border/50" data-testid="expanded-filters">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-4">
                {/* Category */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                    {language === 'ar' ? 'الفئة' : 'Category'}
                  </label>
                  <div className="relative">
                    <Tag size={16} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      data-testid="category-filter"
                      className={`w-full px-4 py-2.5 ${language === 'ar' ? 'pr-9' : 'pl-9'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white text-sm appearance-none`}
                    >
                      <option value="">{t('allCategories')}</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{language === 'ar' ? cat.name_ar : cat.name_en}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none`} />
                  </div>
                </div>

                {/* Country */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                    {language === 'ar' ? 'الدولة' : 'Country'}
                  </label>
                  <div className="relative">
                    <MapPin size={16} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      data-testid="country-filter"
                      className={`w-full px-4 py-2.5 ${language === 'ar' ? 'pr-9' : 'pl-9'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white text-sm appearance-none`}
                    >
                      <option value="">{language === 'ar' ? 'كل الدول' : 'All Countries'}</option>
                      {countries.map(c => (
                        <option key={c.code} value={c.code}>{c.flag} {language === 'ar' ? c.name_ar : c.name_en}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none`} />
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                    {language === 'ar' ? 'نطاق السعر ($)' : 'Price Range ($)'}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="number"
                        value={minPrice}
                        onChange={(e) => setMinPrice(e.target.value)}
                        placeholder={language === 'ar' ? 'من' : 'Min'}
                        data-testid="min-price-input"
                        className="w-full px-3 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                        min="0"
                      />
                    </div>
                    <span className="flex items-center text-muted-foreground text-xs">-</span>
                    <div className="relative flex-1">
                      <input
                        type="number"
                        value={maxPrice}
                        onChange={(e) => setMaxPrice(e.target.value)}
                        placeholder={language === 'ar' ? 'إلى' : 'Max'}
                        data-testid="max-price-input"
                        className="w-full px-3 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                {/* Sort */}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                    {language === 'ar' ? 'ترتيب' : 'Sort By'}
                  </label>
                  <div className="relative">
                    <ArrowUpDown size={16} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      data-testid="sort-filter"
                      className={`w-full px-4 py-2.5 ${language === 'ar' ? 'pr-9' : 'pl-9'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent bg-white text-sm appearance-none`}
                    >
                      {sortOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{language === 'ar' ? opt.label_ar : opt.label_en}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none`} />
                  </div>
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={clearFilters}
                    data-testid="clear-filters-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <RotateCcw size={14} />
                    {language === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-4 mb-6 sm:mb-8 border-b border-border">
          <button
            onClick={() => setActiveTab('offers')}
            data-testid="tab-offers"
            className={`pb-3 sm:pb-4 px-3 sm:px-6 font-medium transition-colors relative text-sm sm:text-base ${
              activeTab === 'offers'
                ? 'text-accent border-b-2 border-accent -mb-px'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <Package size={18} />
              {t('offersTab')} ({offersTotal})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            data-testid="tab-requests"
            className={`pb-3 sm:pb-4 px-3 sm:px-6 font-medium transition-colors relative text-sm sm:text-base ${
              activeTab === 'requests'
                ? 'text-accent border-b-2 border-accent -mb-px'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <div className="flex items-center gap-1.5 sm:gap-2">
              <ShoppingCart size={18} />
              {t('requestsTab')} ({requestsTotal})
            </div>
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="marketplace-loading">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div>
            {activeTab === 'offers' && (
              <>
                {offers.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {offers.map((offer) => (
                      <div key={offer.id} onClick={() => openDetail(offer.id, 'offer')} className="bg-white border border-border rounded-xl card-hover cursor-pointer overflow-hidden" data-testid={`offer-card-${offer.id}`}>
                        {offer.images && offer.images.length > 0 && (
                          <div className="relative">
                            <img src={getImageUrl(offer.images[0])} alt={offer.title} className="w-full h-40 object-cover" data-testid={`offer-image-${offer.id}`} />
                            {offer.images.length > 1 && (
                              <span className="absolute top-2 ${language === 'ar' ? 'left-2' : 'right-2'} px-2 py-0.5 bg-black/60 text-white text-xs rounded-full" data-testid={`offer-image-count-${offer.id}`}>
                                {offer.images.length} +
                              </span>
                            )}
                          </div>
                        )}
                        <div className="p-4 sm:p-6">
                          <div className="flex items-center gap-2.5 mb-2">
                            {offer.company?.picture ? (
                              <img src={getImageUrl(offer.company.picture)} alt="" className="w-8 h-8 rounded-full object-cover border border-border flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                                <Building size={14} className="text-accent/50" />
                              </div>
                            )}
                            <p className="text-sm text-muted-foreground truncate">{offer.company?.name}</p>
                            {offer.company?.avg_rating > 0 && (
                              <div className="flex items-center gap-1 mt-0.5">{renderStars(Math.round(offer.company.avg_rating), 12)}<span className="text-xs text-amber-600 font-medium">{offer.company.avg_rating}</span></div>
                            )}
                          </div>
                          <h3 className="text-lg sm:text-xl font-heading font-bold text-primary mb-2">{offer.title}</h3>
                          {offer.category && (
                            <span className="inline-block px-2 py-1 text-xs bg-muted text-muted-foreground rounded mb-4">
                              {language === 'ar' ? offer.category.name_ar : offer.category.name_en}
                            </span>
                          )}
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-3">{offer.description}</p>
                          <div className="flex items-center justify-between pt-4 border-t border-border">
                            {user ? (
                              <span className="text-xl sm:text-2xl font-heading font-bold text-accent" data-testid={`offer-price-${offer.id}`}>{formatPrice(offer.price, offer.currency)}</span>
                            ) : (
                              <span className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`offer-price-locked-${offer.id}`}>
                                <Lock size={14} />
                                {language === 'ar' ? 'سجّل دخول لعرض السعر' : 'Login to view price'}
                              </span>
                            )}
                            <span className="text-sm text-muted-foreground">{t('quantity')}: {offer.quantity}</span>
                          </div>
                          {offer.company?.country && (() => {
                            const c = getCountryByCode(offer.company.country);
                            return c ? (
                              <div className="flex items-center gap-1.5 mt-2 text-sm text-muted-foreground" data-testid={`offer-country-${offer.id}`}>
                                <span className="text-base">{c.flag}</span>
                                <span>{language === 'ar' ? c.name_ar : c.name_en}</span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20" data-testid="no-results">
                    <Package size={48} className="mx-auto mb-3 text-muted-foreground/30" />
                    <p className="text-muted-foreground">{t('noResults')}</p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="mt-3 text-sm text-accent hover:underline" data-testid="no-results-clear-filters">
                        {language === 'ar' ? 'مسح الفلاتر وعرض الكل' : 'Clear filters and show all'}
                      </button>
                    )}
                  </div>
                )}
                {offersTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8" data-testid="offers-pagination">
                    <button onClick={() => fetchOffers(offersPage - 1)} disabled={offersPage <= 1}
                      className="px-4 py-2 text-sm font-medium border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-colors" data-testid="offers-prev-page">
                      {language === 'ar' ? 'السابق' : 'Previous'}
                    </button>
                    {Array.from({ length: offersTotalPages }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => fetchOffers(p)} data-testid={`offers-page-${p}`}
                        className={`w-10 h-10 text-sm font-medium rounded-lg transition-colors ${p === offersPage ? 'bg-accent text-white' : 'border border-border hover:bg-muted'}`}>
                        {p}
                      </button>
                    ))}
                    <button onClick={() => fetchOffers(offersPage + 1)} disabled={offersPage >= offersTotalPages}
                      className="px-4 py-2 text-sm font-medium border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-colors" data-testid="offers-next-page">
                      {language === 'ar' ? 'التالي' : 'Next'}
                    </button>
                  </div>
                )}
              </>
            )}

            {activeTab === 'requests' && (
              <>
                {requests.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {requests.map((request) => (
                      <div key={request.id} onClick={() => openDetail(request.id, 'request')} className="bg-white border border-border rounded-xl card-hover cursor-pointer overflow-hidden" data-testid={`request-card-${request.id}`}>
                        {request.images && request.images.length > 0 && (
                          <div className="relative">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${request.images[0]}`} alt={request.title} className="w-full h-40 object-cover" data-testid={`request-image-${request.id}`} />
                            {request.images.length > 1 && (
                              <span className="absolute top-2 ${language === 'ar' ? 'left-2' : 'right-2'} px-2 py-0.5 bg-black/60 text-white text-xs rounded-full" data-testid={`request-image-count-${request.id}`}>
                                {request.images.length} +
                              </span>
                            )}
                          </div>
                        )}
                        <div className="p-4 sm:p-6">
                          <div className="flex items-center gap-2.5 mb-2">
                            {request.company?.picture ? (
                              <img src={getImageUrl(request.company.picture)} alt="" className="w-8 h-8 rounded-full object-cover border border-border flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                <Building size={14} className="text-green-500/50" />
                              </div>
                            )}
                            <p className="text-sm text-muted-foreground truncate">{request.company?.name}</p>
                            {request.company?.avg_rating > 0 && (
                              <div className="flex items-center gap-1 mt-0.5">{renderStars(Math.round(request.company.avg_rating), 12)}<span className="text-xs text-amber-600 font-medium">{request.company.avg_rating}</span></div>
                            )}
                          </div>
                          <h3 className="text-lg sm:text-xl font-heading font-bold text-primary mb-2">{request.title}</h3>
                          {request.category && (
                            <span className="inline-block px-2 py-1 text-xs bg-muted text-muted-foreground rounded mb-4">
                              {language === 'ar' ? request.category.name_ar : request.category.name_en}
                            </span>
                          )}
                          <p className="text-muted-foreground text-sm mb-4 line-clamp-3">{request.description}</p>
                          <div className="flex items-center justify-between pt-4 border-t border-border">
                            {user ? (
                              <span className="text-xl sm:text-2xl font-heading font-bold text-green-600" data-testid={`request-budget-${request.id}`}>{formatPrice(request.budget, request.currency)}</span>
                            ) : (
                              <span className="flex items-center gap-1.5 text-sm text-muted-foreground" data-testid={`request-budget-locked-${request.id}`}>
                                <Lock size={14} />
                                {language === 'ar' ? 'سجّل دخول لعرض الميزانية' : 'Login to view budget'}
                              </span>
                            )}
                            <span className="text-sm text-muted-foreground">{t('quantity')}: {request.quantity}</span>
                          </div>
                          {request.company?.country && (() => {
                            const c = getCountryByCode(request.company.country);
                            return c ? (
                              <div className="flex items-center gap-1.5 mt-2 text-sm text-muted-foreground" data-testid={`request-country-${request.id}`}>
                                <span className="text-base">{c.flag}</span>
                                <span>{language === 'ar' ? c.name_ar : c.name_en}</span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20" data-testid="no-results">
                    <ShoppingCart size={48} className="mx-auto mb-3 text-muted-foreground/30" />
                    <p className="text-muted-foreground">{t('noResults')}</p>
                    {hasActiveFilters && (
                      <button onClick={clearFilters} className="mt-3 text-sm text-accent hover:underline" data-testid="no-results-clear-filters">
                        {language === 'ar' ? 'مسح الفلاتر وعرض الكل' : 'Clear filters and show all'}
                      </button>
                    )}
                  </div>
                )}
                {requestsTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8" data-testid="requests-pagination">
                    <button onClick={() => fetchRequests(requestsPage - 1)} disabled={requestsPage <= 1}
                      className="px-4 py-2 text-sm font-medium border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-colors" data-testid="requests-prev-page">
                      {language === 'ar' ? 'السابق' : 'Previous'}
                    </button>
                    {Array.from({ length: requestsTotalPages }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => fetchRequests(p)} data-testid={`requests-page-${p}`}
                        className={`w-10 h-10 text-sm font-medium rounded-lg transition-colors ${p === requestsPage ? 'bg-accent text-white' : 'border border-border hover:bg-muted'}`}>
                        {p}
                      </button>
                    ))}
                    <button onClick={() => fetchRequests(requestsPage + 1)} disabled={requestsPage >= requestsTotalPages}
                      className="px-4 py-2 text-sm font-medium border border-border rounded-lg disabled:opacity-40 hover:bg-muted transition-colors" data-testid="requests-next-page">
                      {language === 'ar' ? 'التالي' : 'Next'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {(selectedItem || detailLoading) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setSelectedItem(null); }} data-testid="detail-modal-overlay">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="detail-modal">
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
              </div>
            ) : selectedItem && (
              <>
                <div className={`p-6 border-b border-border ${selectedItem.type === 'offer' ? 'bg-accent/5' : 'bg-green-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-3 ${
                        selectedItem.type === 'offer' ? 'bg-accent/10 text-accent' : 'bg-green-100 text-green-700'
                      }`}>
                        {selectedItem.type === 'offer' ? t('saleOffers') : t('purchaseRequests')}
                      </span>
                      <h2 className="text-2xl font-heading font-bold text-primary">{selectedItem.title}</h2>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Hash size={12} />{selectedItem.id}</p>
                    </div>
                    <button onClick={() => setSelectedItem(null)} data-testid="close-detail-modal" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <X size={20} className="text-muted-foreground" />
                    </button>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {selectedItem.images && selectedItem.images.length > 0 && (
                    <div className="rounded-xl overflow-hidden border border-border" data-testid="detail-modal-image">
                      <div className="relative">
                        <img
                          src={getImageUrl(selectedItem.images[modalImageIndex])}
                          alt={`${selectedItem.title} - ${modalImageIndex + 1}`}
                          className="w-full h-56 sm:h-72 object-cover transition-opacity duration-200"
                          data-testid={`modal-image-${modalImageIndex}`}
                        />
                        {selectedItem.images.length > 1 && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setModalImageIndex(i => i > 0 ? i - 1 : selectedItem.images.length - 1); }}
                              className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-2' : 'left-2'} w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors`}
                              data-testid="modal-image-prev"
                            >
                              {language === 'ar' ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setModalImageIndex(i => i < selectedItem.images.length - 1 ? i + 1 : 0); }}
                              className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'left-2' : 'right-2'} w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors`}
                              data-testid="modal-image-next"
                            >
                              {language === 'ar' ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                            </button>
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="modal-image-dots">
                              {selectedItem.images.map((_, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => { e.stopPropagation(); setModalImageIndex(idx); }}
                                  className={`w-2 h-2 rounded-full transition-all ${idx === modalImageIndex ? 'bg-white w-5' : 'bg-white/50'}`}
                                  data-testid={`modal-image-dot-${idx}`}
                                />
                              ))}
                            </div>
                            <span className="absolute top-3 right-3 px-2 py-0.5 bg-black/50 text-white text-xs rounded-full" data-testid="modal-image-counter">
                              {modalImageIndex + 1} / {selectedItem.images.length}
                            </span>
                          </>
                        )}
                      </div>
                      {selectedItem.images.length > 1 && (
                        <div className="flex gap-1.5 p-2 bg-gray-50 overflow-x-auto" data-testid="modal-image-thumbnails">
                          {selectedItem.images.map((img, idx) => (
                            <button
                              key={idx}
                              onClick={(e) => { e.stopPropagation(); setModalImageIndex(idx); }}
                              className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-all ${idx === modalImageIndex ? 'border-accent opacity-100' : 'border-transparent opacity-60 hover:opacity-80'}`}
                              data-testid={`modal-thumbnail-${idx}`}
                            >
                              <img src={getImageUrl(img)} alt={`${idx + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {user ? (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl" data-testid="modal-price-visible">
                      <div className="flex items-center gap-2">
                        <DollarSign size={20} className={selectedItem.type === 'offer' ? 'text-accent' : 'text-green-600'} />
                        <span className="text-sm font-medium text-muted-foreground">{selectedItem.type === 'offer' ? t('price') : t('budget')}</span>
                      </div>
                      <span className={`text-3xl font-heading font-bold ${selectedItem.type === 'offer' ? 'text-accent' : 'text-green-600'}`}>
                        {selectedItem.type === 'offer' ? formatPrice(selectedItem.price, selectedItem.currency) : formatPrice(selectedItem.budget, selectedItem.currency)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border-2 border-dashed border-accent/30" data-testid="modal-price-locked">
                      <div className="flex items-center gap-2">
                        <Lock size={20} className="text-accent/60" />
                        <span className="text-sm font-medium text-muted-foreground">{selectedItem.type === 'offer' ? t('price') : t('budget')}</span>
                      </div>
                      <Link to="/login" className="flex items-center gap-1.5 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 text-sm font-semibold transition-all" data-testid="modal-login-for-price">
                        <LogIn size={16} />
                        {language === 'ar' ? 'سجّل دخول' : 'Login'}
                      </Link>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-xs text-muted-foreground mb-1">{t('quantity')}</p>
                      <p className="text-lg font-bold text-primary">{selectedItem.quantity}</p>
                    </div>
                    {selectedItem.category && (
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Tag size={12} />{t('category')}</p>
                        <p className="text-lg font-bold text-primary">{language === 'ar' ? selectedItem.category.name_ar : selectedItem.category.name_en}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">{t('description')}</h3>
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap">{selectedItem.description}</p>
                  </div>
                  {selectedItem.expiration_date && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar size={16} />
                      <span>{t('expirationDate')}: {new Date(selectedItem.expiration_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}</span>
                    </div>
                  )}
                  {selectedItem.is_unlocked ? (
                    selectedItem.company && (
                      <div className="p-5 bg-gray-50 rounded-xl border border-border">
                        <h3 className="text-sm font-semibold text-muted-foreground mb-3">{t('companyName')}</h3>
                        <div className="flex items-center gap-3 mb-2">
                          {selectedItem.company.picture ? (
                            <img src={getImageUrl(selectedItem.company.picture)} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm" />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                              <Building size={20} className="text-accent/40" />
                            </div>
                          )}
                          <h4 className="text-lg font-heading font-bold text-primary">{selectedItem.company.name}</h4>
                        </div>
                        {selectedItem.company.description && (<p className="text-sm text-muted-foreground mb-3">{selectedItem.company.description}</p>)}
                        <div className="space-y-1.5">
                          {selectedItem.company.phone && (<p className="text-sm text-muted-foreground flex items-center gap-2"><Phone size={14} /> {selectedItem.company.phone}</p>)}
                          {selectedItem.company.address && (<p className="text-sm text-muted-foreground flex items-center gap-2"><MapPin size={14} /> {selectedItem.company.address}</p>)}
                          {selectedItem.company.country && (() => {
                            const c = getCountryByCode(selectedItem.company.country);
                            return c ? (<p className="text-sm text-muted-foreground flex items-center gap-2"><span className="text-base">{c.flag}</span> {language === 'ar' ? c.name_ar : c.name_en}</p>) : null;
                          })()}
                        </div>
                        {user && selectedItem.company_id !== user.company_id && (
                          <Link to={`/messages?to=${selectedItem.company_id}&name=${encodeURIComponent(selectedItem.company?.name || '')}`} className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 text-sm font-semibold transition-all">
                            <Mail size={16} />{t('directMessaging')}
                          </Link>
                        )}

                        {/* Company Rating Summary */}
                        {(selectedItem.company.avg_rating > 0 || selectedItem.company.review_count > 0) && (
                          <div className="mt-3 flex items-center gap-2">
                            {renderStars(Math.round(selectedItem.company.avg_rating || 0))}
                            <span className="text-sm font-bold text-amber-600">{selectedItem.company.avg_rating || 0}</span>
                            <span className="text-xs text-muted-foreground">({selectedItem.company.review_count || 0} {language === 'ar' ? 'تقييم' : 'reviews'})</span>
                          </div>
                        )}
                      </div>
                    )
                  ) : (
                    <div className="p-5 bg-gray-50 rounded-xl border-2 border-dashed border-accent/40 text-center" data-testid="locked-company-info">
                      <Lock size={40} className="mx-auto mb-3 text-accent/60" />
                      <h3 className="text-lg font-heading font-bold text-primary mb-2">{language === 'ar' ? 'معلومات الشركة مخفية' : 'Company Info Hidden'}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{language === 'ar' ? 'سجّل دخولك لرؤية معلومات الاتصال الكاملة للشركة' : 'Login to view full company contact details'}</p>
                      <Link to="/login" data-testid="login-to-unlock-btn" className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 font-bold transition-all shadow-sm hover:shadow-md">
                        <LogIn size={20} />{language === 'ar' ? 'تسجيل الدخول' : 'Login'}
                      </Link>
                    </div>
                  )}
                  {/* Reviews Section */}
                  {selectedItem.is_unlocked && selectedItem.company_id && (
                    <div className="space-y-4" data-testid="reviews-section">
                      <h3 className="text-sm font-heading font-bold text-primary flex items-center gap-2">
                        <Star size={16} className="text-amber-400" />
                        {language === 'ar' ? 'التقييمات' : 'Reviews'} ({reviews.length})
                      </h3>

                      {/* Submit Review Form */}
                      {user && selectedItem.company_id !== user.company_id && (
                        <div className="p-4 bg-gray-50 rounded-xl border border-border" data-testid="review-form">
                          <p className="text-sm font-medium text-foreground mb-2">{language === 'ar' ? 'قيّم هذه الشركة' : 'Rate this company'}</p>
                          <div className="flex items-center gap-1 mb-3" data-testid="star-rating-input">
                            {[1, 2, 3, 4, 5].map(i => (
                              <button key={i} type="button" onClick={() => setReviewRating(i)} onMouseEnter={() => setReviewHover(i)} onMouseLeave={() => setReviewHover(0)}
                                data-testid={`star-${i}`} className="p-0.5 transition-transform hover:scale-110">
                                <Star size={24} className={`transition-colors ${i <= (reviewHover || reviewRating) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                              </button>
                            ))}
                            {reviewRating > 0 && <span className="text-sm font-bold text-amber-600 ml-2">{reviewRating}/5</span>}
                          </div>
                          <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                            placeholder={language === 'ar' ? 'أضف تعليقك (اختياري)...' : 'Add a comment (optional)...'}
                            data-testid="review-comment-input" rows={2}
                            className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent resize-none mb-2" />
                          <button onClick={submitReview} disabled={reviewRating === 0 || reviewSubmitting}
                            data-testid="submit-review-btn"
                            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600 disabled:opacity-50 transition-all">
                            {reviewSubmitting ? (language === 'ar' ? 'جاري الإرسال...' : 'Submitting...') : (language === 'ar' ? 'إرسال التقييم' : 'Submit Review')}
                          </button>
                        </div>
                      )}

                      {/* Reviews List */}
                      {reviews.length > 0 ? (
                        <div className="space-y-3" data-testid="reviews-list">
                          {reviews.map((rev) => (
                            <div key={rev.id} className="p-3 bg-white border border-border rounded-lg" data-testid={`review-${rev.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  {renderStars(rev.rating, 14)}
                                  <span className="text-sm font-medium text-foreground">{rev.user_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">{new Date(rev.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                  {(user?.id === rev.user_id || user?.role === 'admin') && (
                                    <button onClick={() => deleteReview(rev.id)} data-testid={`delete-review-${rev.id}`} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                                  )}
                                </div>
                              </div>
                              {rev.comment && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{rev.comment}</p>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4" data-testid="no-reviews">
                          {language === 'ar' ? 'لا توجد تقييمات بعد' : 'No reviews yet'}
                        </p>
                      )}
                    </div>
                  )}

                  {selectedItem.created_at && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(selectedItem.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
